import { useState, useEffect } from 'react';
import { motion } from 'motion/react';
import { snappyTween } from './motionUi';
import {
  clearStoredProgress,
  getMusicVolumePct,
  getSoundVolumePct,
  getStoredNickname,
  hasStoredProgress,
  setMusicVolumePct,
  setSoundVolumePct,
  PREFS_CHANGED
} from '../gamePrefs';
import { deleteUserStateOnServer } from '../gameSessionApi';

function VolumeRow({ id, label, value, onChange }) {
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between gap-2">
        <label htmlFor={id} className="text-[10px] font-semibold uppercase tracking-wide text-game-muted">
          {label}
        </label>
        <span className="text-xs tabular-nums text-game-body">{value}%</span>
      </div>
      <input
        id={id}
        type="range"
        min={0}
        max={100}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="h-2 w-full cursor-pointer appearance-none rounded-full bg-white/10 accent-game-accent [&::-webkit-slider-thumb]:h-3.5 [&::-webkit-slider-thumb]:w-3.5 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-game-accent [&::-moz-range-thumb]:h-3.5 [&::-moz-range-thumb]:w-3.5 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:border-0 [&::-moz-range-thumb]:bg-game-accent"
      />
    </div>
  );
}

export default function GameSettingsModal({
  onOpenNickname = () => {},
  profileDisplayName,
  serverPlayerId = '',
  onPurgeComplete = () => {}
}) {
  const [sound, setSound] = useState(() => getSoundVolumePct());
  const [music, setMusic] = useState(() => getMusicVolumePct());
  const [displayName, setDisplayName] = useState(() =>
    profileDisplayName !== undefined ? String(profileDisplayName ?? '') : getStoredNickname()
  );
  const [purgeAsk, setPurgeAsk] = useState(false);
  const [purgeDone, setPurgeDone] = useState(false);
  const [purgeBusy, setPurgeBusy] = useState(false);
  const [purgeError, setPurgeError] = useState('');

  useEffect(() => {
    if (profileDisplayName !== undefined) {
      setDisplayName(String(profileDisplayName ?? ''));
    }
  }, [profileDisplayName]);

  useEffect(() => {
    const sync = () => {
      setSound(getSoundVolumePct());
      setMusic(getMusicVolumePct());
      if (profileDisplayName === undefined) {
        setDisplayName(getStoredNickname());
      }
    };
    window.addEventListener(PREFS_CHANGED, sync);
    return () => window.removeEventListener(PREFS_CHANGED, sync);
  }, [profileDisplayName]);

  const onSound = (v) => {
    setSound(v);
    setSoundVolumePct(v);
  };

  const onMusic = (v) => {
    setMusic(v);
    setMusicVolumePct(v);
  };

  const confirmPurge = async () => {
    setPurgeError('');
    const pid = String(serverPlayerId || '').trim();
    if (pid) {
      setPurgeBusy(true);
      try {
        await deleteUserStateOnServer(pid);
      } catch (e) {
        console.error(e);
        setPurgeError(
          e instanceof Error ? e.message : 'Could not delete server save. Check connection and try again.'
        );
        setPurgeBusy(false);
        return;
      }
      setPurgeBusy(false);
    }
    clearStoredProgress();
    setPurgeAsk(false);
    setPurgeDone(true);
    onPurgeComplete();
    window.setTimeout(() => setPurgeDone(false), 2500);
  };

  return (
    <motion.div
      className="space-y-6 p-5"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.2 }}
    >
      <section className="space-y-4">
        <p className="text-[10px] font-semibold uppercase tracking-wide text-game-muted">Audio</p>
        <VolumeRow id="game-settings-sound" label="Sound" value={sound} onChange={onSound} />
        <VolumeRow id="game-settings-music" label="Music" value={music} onChange={onMusic} />
      </section>

      <section className="space-y-3">
        <p className="text-[10px] font-semibold uppercase tracking-wide text-game-muted">Profile</p>
        <div className="flex items-center justify-between gap-3 rounded-xl border border-white/10 bg-game-surface/80 px-3 py-2.5">
          <div className="min-w-0 flex-1">
            <p className="text-[10px] text-game-muted">Display name</p>
            <p className="truncate text-sm font-semibold text-game-text">{displayName}</p>
          </div>
          <motion.button
            type="button"
            transition={snappyTween}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={onOpenNickname}
            className="shrink-0 rounded-xl border border-white/15 bg-game-popup/40 px-3 py-2 text-xs font-semibold text-game-text hover:border-game-accent/35 hover:bg-game-accent/10"
          >
            Change
          </motion.button>
        </div>
      </section>

      <section className="space-y-2 border-t border-white/10 pt-4">
        <p className="text-[10px] font-semibold uppercase tracking-wide text-game-muted">Data</p>
        {purgeError ? <p className="text-xs text-red-300">{purgeError}</p> : null}
        {purgeDone ? (
          <p className="text-xs text-game-accent">Progress cleared on server and this device.</p>
        ) : null}
        {!purgeAsk ? (
          <motion.button
            type="button"
            transition={snappyTween}
            whileHover={{ scale: 1.01 }}
            whileTap={{ scale: 0.99 }}
            onClick={() => setPurgeAsk(true)}
            className="w-full rounded-xl border border-red-500/35 bg-red-500/10 py-2.5 text-sm font-semibold text-red-300"
          >
            Delete progress
          </motion.button>
        ) : (
          <div className="space-y-2 rounded-xl border border-red-500/30 bg-red-950/20 p-3">
            <p className="text-xs leading-relaxed text-game-body">
              Deletes your save on the server (progress, chat memory, promo redemptions for this ID) and clears
              this device. This cannot be undone.
            </p>
            <div className="flex gap-2">
              <button
                type="button"
                disabled={purgeBusy}
                onClick={() => setPurgeAsk(false)}
                className="flex-1 rounded-lg border border-white/15 py-2 text-xs font-semibold text-game-text hover:bg-white/5 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={purgeBusy}
                onClick={() => void confirmPurge()}
                className="flex-1 rounded-lg bg-red-600 py-2 text-xs font-bold text-white hover:bg-red-500 disabled:opacity-60"
              >
                {purgeBusy ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        )}
        {!hasStoredProgress() && !purgeAsk && !purgeDone ? (
          <p className="text-[10px] text-game-muted">No local save file yet.</p>
        ) : null}
      </section>
    </motion.div>
  );
}
